<template>
  <div class="vue-pincode-input-wrapper">
    <div class="vue-pincode-input__item" v-for="(cell, index) in cells" :key="cell.key">

    <div class="vue-pincode-input__dash"  v-if="index == 2 && cells.length == 6">-</div>
    <div class="vue-pincode-input__dash"  v-if="index == 4 && cells.length == 6">-</div>
    <div class="vue-pincode-input__dash" v-if="index == 6 && cells.length == 9">-</div>
    <div class="vue-pincode-input__dash"  v-if="index == 4 && (cells.length == 8)">-</div>
    <input
      :ref="`${baseRefName}${index}`"
      v-model.trim="cell.value"
      v-bind="$attrs"
      class="vue-pincode-input form-control"
      :type="cellsInputTypes[index]"
      @focus="focusedCellIdx = index;"
      @keydown.delete="onCellErase(index, $event)"
      @keydown="onKeyDown"
      v-uppercase
    >
  </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

const BASE_REF_NAME = 'vue-pincode-input';
const CELL_REGEXP = '^[a-zA-Z0-9]{1}$';
const DEFAULT_INPUT_TYPE = 'text';
const SECURE_INPUT_TYPE = 'password';

export default {
    name:'PincodeInput',
    props:{
     value: {
       type: String,
       required: true,
     },
     length: {
       type: Number,
       default: 4,
     },
     autofocus: {
       type: Boolean,
       default: true,
     },
     secure: {
       type: Boolean,
       default: false,
     },
     characterPreview: {
       type: Boolean,
       default: true,
     },
     charPreviewDuration: {
       type: Number,
       default: 300,
     }
    },
    data() {
        return {
          baseRefName: BASE_REF_NAME,
          focusedCellIdx: 0,
          cells: [],
          watchers: {},
          cellsInputTypes: {},
        }
    },

  computed: {
    pinCodeComputed() {
      return this.cells.reduce((pin, cell) => pin.toUpperCase() + cell.value.toUpperCase(), '');
    },
  },

  watch: {
    value(value) {
      if (value) {
        this.onParentValueUpdated();
      } else {
        this.reset();
      }
    },

    length() {
      this.reset();
    },

    pinCodeComputed(val) {
      this.$emit('input', val);
    },
  },

  mounted() {
    this.init();
    this.onParentValueUpdated();

    if (this.autofocus) {
      this.$nextTick(this.focusCellByIndex);
    }
  },

  methods: {
    /* init stuff */

    init() {
      const inputType = this.getRelevantInputType();
      for (let key = 0; key < this.length; key += 1) {
        this.setCellObject(key);
        this.setCellInputType(key, inputType);
        this.setCellWatcher(key);
      }
    },

    setCellObject(key) {
      this.$set(this.cells, key, { key, value: '' });
    },

    setCellInputType(index, inputType) {
      this.$set(this.cellsInputTypes, index, inputType);
    },

    setCellWatcher(index) {
      const watchingProperty = `cells.${index}.value`;

      this.watchers[watchingProperty] = this.$watch(
        watchingProperty,
        (newVal, oldVal) => this.onCellChanged(index, newVal.toUpperCase(), oldVal),
      );
    },

    /* handlers */

    onParentValueUpdated() {
      if (this.value.length !== this.length) {
        this.$emit('input', this.pinCodeComputed);
        return;
      }

      this.value
        .split('')
        .forEach((cell, idx) => {
          this.cells[idx].value = cell || '';
        });
    },

    onCellChanged(index, newVal, oldVal) {
      newVal = newVal.toUpperCase();
      if (!this.isTheCellValid(newVal, false)) {
        this.cells[index].value = '';
        return;
      }

      this.focusNextCell();

      /* performing character preview if it's enabled */
      if (this.secure && this.characterPreview) {
        setTimeout(this.setCellInputType, this.charPreviewDuration, index);
      }
    },

    onCellErase(index, e) {
      const isThisCellFilled = this.cells[index].value.length;

      if (!isThisCellFilled) {
        this.focusPreviousCell();
        e.preventDefault();
      }
    },

    onKeyDown(e) {
      switch (e.keyCode) {



        /* left arrow key */
        case 37:
          this.focusPreviousCell();
          break;

        /* right arrow key */
        case 39:
          this.focusNextCell();
          break;

        default:
          break;
      }
    },

    /* reset stuff */

    reset() {
      this.unwatchCells();
      this.init();
      this.focusCellByIndex();
    },

    unwatchCells() {
      const watchers = Object.keys(this.watchers);
      watchers.forEach(name => this.watchers[name]());
    },

    /* helpers */

    isTheCellValid(cell, allowEmpty = true) {
      if (!cell) {
        return allowEmpty ? cell === '' : false;
      }

      return !!cell.match(CELL_REGEXP);
    },

    getRelevantInputType() {
      return this.secure && !this.characterPreview
        ? SECURE_INPUT_TYPE
        : DEFAULT_INPUT_TYPE;
    },

    focusPreviousCell() {
      if (!this.focusedCellIdx) return;

      this.focusCellByIndex(this.focusedCellIdx - 1);
    },

    focusNextCell() {
      if (this.focusedCellIdx === this.length - 1) return;

      this.focusCellByIndex(this.focusedCellIdx + 1);
    },

    focusCellByIndex(index = 0) {
      const ref = `${this.baseRefName}${index}`;
      if (this.$refs[ref]) {
        const el = (this.$refs[ref])[0];
        el.focus();
        el.select();
      }

      this.focusedCellIdx = index;
    },
  },
}
</script>

<style>
.vue-pincode-input-wrapper {
  display: inline-flex;
}

.vue-pincode-input {
  margin-right: 3px;
  max-width: 40px;
}
.vue-pincode-input__item {
  display:inline-flex;
}
.vue-pincode-input__dash {
  padding:5px;
}
.vue-pincode-input:focus {
}
</style>
